import React, { useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import { AIOutlineButton } from '../components/ui/AIOutlineButton';
import { AISidebar } from '../components/AISidebar';
import { AIRecommendationPanel } from '../components/ai/AIRecommendationPanel';
import { useAdminData } from '../contexts/AdminDataContext';
import { getGeneralRecommendations, getAppUsageRecommendations, getPointsRecommendations, getTopUsersRecommendations, getTopPartnersRecommendations, getPendingProofsRecommendations } from '../utils/aiRecommendationsData';
import { Users, Star, Gem, TrendingDown, Download, CheckCircle, Clock, Activity } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Mock data - Utilizzo app ultimi 7 giorni
const appUsageData7Days = [{
  day: 'Lun',
  users: 245
}, {
  day: 'Mar',
  users: 312
}, {
  day: 'Mer',
  users: 289
}, {
  day: 'Gio',
  users: 356
}, {
  day: 'Ven',
  users: 398
}, {
  day: 'Sab',
  users: 421
}, {
  day: 'Dom',
  users: 378
}];
const appUsageData30Days = [{
  day: '1',
  users: 245
}, {
  day: '5',
  users: 312
}, {
  day: '10',
  users: 289
}, {
  day: '15',
  users: 356
}, {
  day: '20',
  users: 398
}, {
  day: '25',
  users: 421
}, {
  day: '30',
  users: 378
}];
const appUsageData90Days = [{
  day: 'Gen',
  users: 2450
}, {
  day: 'Feb',
  users: 2780
}, {
  day: 'Mar',
  users: 2890
}];
// Mock data - Punti raccolti per periodo
const pointsData7Days = [{
  day: 'Lun',
  stella: 12500,
  diamante: 4200
}, {
  day: 'Mar',
  stella: 15600,
  diamante: 5100
}, {
  day: 'Mer',
  stella: 14200,
  diamante: 4800
}, {
  day: 'Gio',
  stella: 17800,
  diamante: 6200
}, {
  day: 'Ven',
  stella: 19900,
  diamante: 7100
}, {
  day: 'Sab',
  stella: 21100,
  diamante: 7800
}, {
  day: 'Dom',
  stella: 18900,
  diamante: 6900
}];
const pointsData30Days = [{
  day: '1',
  stella: 45000,
  diamante: 18000
}, {
  day: '5',
  stella: 52000,
  diamante: 21000
}, {
  day: '10',
  stella: 48000,
  diamante: 19500
}, {
  day: '15',
  stella: 56000,
  diamante: 23000
}, {
  day: '20',
  stella: 61000,
  diamante: 25500
}, {
  day: '25',
  stella: 58000,
  diamante: 24000
}, {
  day: '30',
  stella: 63000,
  diamante: 26500
}];
const pointsData90Days = [{
  day: 'Gen',
  stella: 450000,
  diamante: 180000
}, {
  day: 'Feb',
  stella: 520000,
  diamante: 210000
}, {
  day: 'Mar',
  stella: 580000,
  diamante: 240000
}];
// Mock data - Prove in attesa di approvazione
const pendingProofs = [{
  id: 1,
  user: 'Marco Rossi',
  avatar: 'https://i.pravatar.cc/150?img=13',
  action: 'Raccolta differenziata',
  puntiDiamante: 50,
  data: '2 ore fa',
  immagine: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400'
}, {
  id: 2,
  user: 'Giulia Bianchi',
  avatar: 'https://i.pravatar.cc/150?img=10',
  action: 'Utilizzo borraccia',
  puntiDiamante: 40,
  data: '4 ore fa',
  immagine: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400'
}, {
  id: 3,
  user: 'Lorenzo Gallo',
  avatar: 'https://i.pravatar.cc/150?img=17',
  action: 'Mobilità sostenibile',
  puntiDiamante: 50,
  data: '5 ore fa',
  immagine: 'https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=400'
}];
const CRMDashboard = () => {
  const [usagePeriod, setUsagePeriod] = useState('7days');
  const [pointsPeriod, setPointsPeriod] = useState('7days');
  const [proofs, setProofs] = useState(pendingProofs);
  const [processingProofId, setProcessingProofId] = useState<number | null>(null);
  // Get users and partners from AdminDataContext
  const {
    users,
    partners,
    showToast
  } = useAdminData();
  // Calculate top users dynamically from real data
  const topUsers = useMemo(() => {
    return users.filter(user => user.status === 'active').sort((a, b) => {
      // Sort by total points collected (stella + diamante)
      const totalA = a.creditiStellaOttenuti + a.creditiDiamanteOttenuti;
      const totalB = b.creditiStellaOttenuti + b.creditiDiamanteOttenuti;
      return totalB - totalA;
    }).slice(0, 5).map(user => ({
      id: user.id,
      avatar: user.avatar,
      nome: `${user.nome} ${user.cognome}`,
      puntiStella: user.creditiStellaOttenuti,
      puntiDiamante: user.creditiDiamanteOttenuti
    }));
  }, [users]);
  // Calculate top partners dynamically from real data
  const topPartners = useMemo(() => {
    return partners.filter(partner => partner.status === 'active').sort((a, b) => b.totalRedemptions - a.totalRedemptions).slice(0, 5).map(partner => ({
      id: partner.id,
      nome: partner.name,
      logo: partner.logo,
      premi: partner.activeRewards,
      riscatti: partner.totalRedemptions
    }));
  }, [partners]);
  // AI Sidebar state
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [aiContext, setAiContext] = useState<'general' | 'app-usage' | 'points' | 'top-users' | 'top-partners' | 'pending-proofs'>('general');
  const getUsageData = () => {
    switch (usagePeriod) {
      case '30days':
        return appUsageData30Days;
      case '90days':
        return appUsageData90Days;
      default:
        return appUsageData7Days;
    }
  };
  const getPointsData = () => {
    switch (pointsPeriod) {
      case '30days':
        return pointsData30Days;
      case '90days':
        return pointsData90Days;
      default:
        return pointsData7Days;
    }
  };
  // Get recommendations based on context
  const getRecommendations = () => {
    const baseRecs = (() => {
      switch (aiContext) {
        case 'app-usage':
          return getAppUsageRecommendations();
        case 'points':
          return getPointsRecommendations();
        case 'top-users':
          return getTopUsersRecommendations();
        case 'top-partners':
          return getTopPartnersRecommendations();
        case 'pending-proofs':
          return getPendingProofsRecommendations();
        default:
          return getGeneralRecommendations();
      }
    })();
    return baseRecs.map(rec => ({
      ...rec,
      onCtaClick: () => console.log('CTA clicked:', rec.id)
    }));
  };
  const getSidebarTitle = () => {
    switch (aiContext) {
      case 'app-usage':
        return 'Suggerimenti AI: Utilizzo App';
      case 'points':
        return 'Suggerimenti AI: Punti Raccolti';
      case 'top-users':
        return 'Suggerimenti AI: Top Users';
      case 'top-partners':
        return 'Suggerimenti AI: Top Partners';
      case 'pending-proofs':
        return 'Suggerimenti AI: Prove in Attesa';
      default:
        return 'Suggerimenti AI: Dashboard';
    }
  };
  const handleAIClick = (context: typeof aiContext) => {
    setAiContext(context);
    setIsAISidebarOpen(true);
  };
  const handleApproveProof = (proofId: number) => {
    const proof = proofs.find(p => p.id === proofId);
    if (!proof) return;
    setProcessingProofId(proofId);
    // Simulate API call
    setTimeout(() => {
      setProofs(proofs.filter(p => p.id !== proofId));
      setProcessingProofId(null);
      showToast(`Prova di ${proof.user} approvata! +${proof.puntiDiamante} punti diamante assegnati.`);
    }, 500);
  };
  const handleRejectProof = (proofId: number) => {
    const proof = proofs.find(p => p.id === proofId);
    if (!proof) return;
    setProcessingProofId(proofId);
    // Simulate API call
    setTimeout(() => {
      setProofs(proofs.filter(p => p.id !== proofId));
      setProcessingProofId(null);
      showToast(`Prova di ${proof.user} rifiutata.`);
    }, 500);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-2122">
      <div className="w-20 flex-shrink-0" data-id="element-2123">
        <Sidebar data-id="element-2124" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2125">
        <Topbar data-id="element-2126" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2127">
          {/* Page Header with AI Button */}
          <div className="mb-8" data-id="element-2128">
            <div className="flex items-start justify-between" data-id="element-2129">
              <div data-id="element-2130">
                <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2131">
                  Dashboard CRM
                </h1>
                <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-2132">
                  Panoramica utenti e attività sulla piattaforma
                </p>
              </div>
              <AIOutlineButton onClick={() => handleAIClick('general')} data-id="element-2133">
                Suggerimenti AI
              </AIOutlineButton>
            </div>
          </div>

          {/* KPI Cards - Changed gap-4 to gap-6 for consistency with widgets */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2134">
            <MetricCard title="Utenti Attivi" value="2,847" change="12.5%" isPositive={true} icon={Users} subtitle="Ultimi 30 giorni" data-id="element-2135" />
            <MetricCard title="Punti Stella Raccolti" value="845,230" change="18.3%" isPositive={true} icon={Star} subtitle="Ultimi 30 giorni" data-id="element-2136" />
            <MetricCard title="Punti Diamante Raccolti" value="312,450" change="22.7%" isPositive={true} icon={Gem} subtitle="Ultimi 30 giorni" data-id="element-2137" />
            <MetricCard title="Punti Spesi" value="892,340" change="15.8%" isPositive={true} icon={TrendingDown} subtitle="Ultimi 30 giorni" data-id="element-2138" />
          </div>

          {/* Charts Row - Utilizzo App and Punti Raccolti - Changed rounded-lg to rounded-sm */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-2139">
            {/* Utilizzo App Chart */}
            <div className="rounded-sm border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2140">
              <div className="flex items-center justify-between mb-4" data-id="element-2141">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-2142">
                  Utilizzo App
                </h3>
                <div className="flex items-center gap-2" data-id="element-2143">
                  <select className="text-sm font-normal border border-gray-300 rounded-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-greenback" value={usagePeriod} onChange={e => setUsagePeriod(e.target.value)} data-id="element-2144">
                    <option value="7days" data-id="element-2145">Ultimi 7 giorni</option>
                    <option value="30days" data-id="element-2146">Ultimi 30 giorni</option>
                    <option value="90days" data-id="element-2147">Ultimi 3 mesi</option>
                  </select>
                  <AIOutlineButton onClick={() => handleAIClick('app-usage')} className="text-xs px-3 py-1.5" data-id="element-2148">
                    Suggerimenti AI
                  </AIOutlineButton>
                </div>
              </div>
              <div className="h-80" data-id="element-2149">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2150">
                  <BarChart data={getUsageData()} data-id="element-2151">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2152" />
                    <XAxis dataKey="day" stroke="#6B7280" fontSize={12} data-id="element-2153" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-2154" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.125rem'
                  }} data-id="element-2155" />
                    <Bar dataKey="users" fill="#108d3e" radius={[4, 4, 0, 0]} data-id="element-2156" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Punti Raccolti Chart */}
            <div className="rounded-sm border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2157">
              <div className="flex items-center justify-between mb-4" data-id="element-2158">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-2159">
                  Punti Raccolti
                </h3>
                <div className="flex items-center gap-2" data-id="element-2160">
                  <select className="text-sm font-normal border border-gray-300 rounded-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-greenback" value={pointsPeriod} onChange={e => setPointsPeriod(e.target.value)} data-id="element-2161">
                    <option value="7days" data-id="element-2162">Ultimi 7 giorni</option>
                    <option value="30days" data-id="element-2163">Ultimi 30 giorni</option>
                    <option value="90days" data-id="element-2164">Ultimi 3 mesi</option>
                  </select>
                  <AIOutlineButton onClick={() => handleAIClick('points')} className="text-xs px-3 py-1.5" data-id="element-2165">
                    Suggerimenti AI
                  </AIOutlineButton>
                </div>
              </div>
              <div className="h-80" data-id="element-2166">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2167">
                  <LineChart data={getPointsData()} data-id="element-2168">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2169" />
                    <XAxis dataKey="day" stroke="#6B7280" fontSize={12} data-id="element-2170" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-2171" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.125rem'
                  }} data-id="element-2172" />
                    <Line type="monotone" dataKey="stella" stroke="#EAB308" strokeWidth={2} dot={{
                    fill: '#EAB308',
                    r: 4
                  }} name="Punti Stella" data-id="element-2173" />
                    <Line type="monotone" dataKey="diamante" stroke="#3B82F6" strokeWidth={2} dot={{
                    fill: '#3B82F6',
                    r: 4
                  }} name="Punti Diamante" data-id="element-2174" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4" data-id="element-2175">
                <div className="flex items-center" data-id="element-2176">
                  <Star className="h-4 w-4 text-yellow-500 mr-2" data-id="element-2177" />
                  <span className="text-sm font-normal text-gray-600" data-id="element-2178">
                    Punti Stella
                  </span>
                </div>
                <div className="flex items-center" data-id="element-2179">
                  <Gem className="h-4 w-4 text-blue-500 mr-2" data-id="element-2180" />
                  <span className="text-sm font-normal text-gray-600" data-id="element-2181">
                    Punti Diamante
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Users and Top Partners Side by Side - Changed rounded-lg to rounded-sm */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-2182">
            {/* Top Users - NOW USING REAL DATA */}
            <div className="rounded-sm border border-gray-200 bg-white overflow-hidden" data-id="element-2183">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-2184">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-2185">
                  Top Users (Più Attivi)
                </h3>
                <AIOutlineButton onClick={() => handleAIClick('top-users')} className="text-xs px-3 py-1.5" data-id="element-2186">
                  Suggerimenti AI
                </AIOutlineButton>
              </div>
              <div className="overflow-x-auto" data-id="element-2187">
                <table className="w-full" data-id="element-2188">
                  <thead data-id="element-2189">
                    <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2190">
                      <th className="px-6 py-3" data-id="element-2191">Utente</th>
                      <th className="px-6 py-3" data-id="element-2192">
                        <div className="flex items-center" data-id="element-2193">
                          <Star className="h-3 w-3 mr-1 text-yellow-500" data-id="element-2194" />
                          Stella
                        </div>
                      </th>
                      <th className="px-6 py-3" data-id="element-2195">
                        <div className="flex items-center" data-id="element-2196">
                          <Gem className="h-3 w-3 mr-1 text-blue-500" data-id="element-2197" />
                          Diamante
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-2198">
                    {topUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-2199">
                        <td className="px-6 py-4" data-id="element-2200">
                          <div className="flex items-center" data-id="element-2201">
                            <img src={user.avatar} alt={user.nome} className="h-8 w-8 rounded-full object-cover mr-3" data-id="element-2202" />
                            <div className="text-sm font-medium text-gray-900" data-id="element-2203">
                              {user.nome}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-yellow-600" data-id="element-2204">
                          {user.puntiStella.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-blue-600" data-id="element-2205">
                          {user.puntiDiamante.toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Partners - USING REAL DATA */}
            <div className="rounded-sm border border-gray-200 bg-white overflow-hidden" data-id="element-2206">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-2207">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-2208">
                  Top Partners (Più Scelti)
                </h3>
                <AIOutlineButton onClick={() => handleAIClick('top-partners')} className="text-xs px-3 py-1.5" data-id="element-2209">
                  Suggerimenti AI
                </AIOutlineButton>
              </div>
              <div className="overflow-x-auto" data-id="element-2210">
                <table className="w-full" data-id="element-2211">
                  <thead data-id="element-2212">
                    <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2213">
                      <th className="px-6 py-3" data-id="element-2214">Partner</th>
                      <th className="px-6 py-3" data-id="element-2215">Premi</th>
                      <th className="px-6 py-3" data-id="element-2216">Riscatti</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-2217">
                    {topPartners.map(partner => <tr key={partner.id} className="hover:bg-gray-50" data-id="element-2218">
                        <td className="px-6 py-4" data-id="element-2219">
                          <div className="flex items-center" data-id="element-2220">
                            <div className="h-8 w-8 rounded-sm border border-gray-200 bg-white flex items-center justify-center p-1 mr-3 flex-shrink-0" data-id="element-2221">
                              <img src={partner.logo} alt={partner.nome} className="max-h-full max-w-full object-contain" onError={e => {
                            e.currentTarget.src = 'https://via.placeholder.com/32?text=Logo';
                          }} data-id="element-2222" />
                            </div>
                            <div className="text-sm font-medium text-gray-900" data-id="element-2223">
                              {partner.nome}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-2224">
                          {partner.premi?.toLocaleString() ?? '-'}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-2225">
                          {partner.riscatti.toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Prove in Attesa di Approvazione - Changed rounded-lg to rounded-sm */}
          <div className="rounded-sm border border-gray-200 bg-white overflow-hidden" data-id="element-2226">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-2227">
              <div data-id="element-2228">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-2229">
                  Prove in Attesa di Approvazione
                </h3>
                <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-2230">
                  {proofs.length} prove da verificare
                </p>
              </div>
              <AIOutlineButton onClick={() => handleAIClick('pending-proofs')} className="text-xs px-3 py-1.5" data-id="element-2231">
                Suggerimenti AI
              </AIOutlineButton>
            </div>
            {proofs.length === 0 ? <div className="p-12 text-center" data-id="element-2232">
                <CheckCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-2233" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-2234">
                  Nessuna prova in attesa
                </h3>
                <p className="text-sm font-normal text-gray-500" data-id="element-2235">
                  Tutte le prove sono state verificate
                </p>
              </div> : <div className="divide-y divide-gray-200" data-id="element-2236">
                {proofs.map(proof => <div key={proof.id} className={`p-6 transition-all ${processingProofId === proof.id ? 'opacity-50 pointer-events-none' : 'hover:bg-gray-50'}`} data-id="element-2237">
                    <div className="flex items-start gap-4" data-id="element-2238">
                      <img src={proof.avatar} alt={proof.user} className="h-12 w-12 rounded-full object-cover" data-id="element-2239" />
                      <div className="flex-1" data-id="element-2240">
                        <div className="flex items-start justify-between" data-id="element-2241">
                          <div data-id="element-2242">
                            <h4 className="font-semibold text-gray-900" data-id="element-2243">
                              {proof.user}
                            </h4>
                            <p className="text-sm font-normal text-gray-600 mt-1" data-id="element-2244">
                              {proof.action}
                            </p>
                            <div className="flex items-center gap-3 mt-2" data-id="element-2245">
                              <div className="flex items-center text-sm" data-id="element-2246">
                                <Gem className="h-4 w-4 mr-1 text-blue-500" data-id="element-2247" />
                                <span className="font-medium text-blue-600" data-id="element-2248">
                                  +{proof.puntiDiamante}
                                </span>
                              </div>
                              <span className="text-xs font-normal text-gray-500" data-id="element-2249">
                                {proof.data}
                              </span>
                            </div>
                          </div>
                          <img src={proof.immagine} alt="Prova" className="h-20 w-20 rounded-sm object-cover" data-id="element-2250" />
                        </div>
                        <div className="flex gap-2 mt-4" data-id="element-2251">
                          <button onClick={() => handleApproveProof(proof.id)} disabled={processingProofId === proof.id} className="px-4 py-2 bg-greenback text-white rounded-sm text-sm font-medium hover:bg-greenback/90 flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-2252">
                            <CheckCircle className="h-4 w-4 mr-2" data-id="element-2253" />
                            {processingProofId === proof.id ? 'Approvazione...' : 'Approva'}
                          </button>
                          <button onClick={() => handleRejectProof(proof.id)} disabled={processingProofId === proof.id} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-sm text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-2254">
                            {processingProofId === proof.id ? 'Rifiuto...' : 'Rifiuta'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </div>
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title={getSidebarTitle()} data-id="element-2255">
        <AIRecommendationPanel recommendations={getRecommendations()} data-id="element-2256" />
      </AISidebar>
    </div>;
};
export default CRMDashboard;